import React, { useMemo } from "react";
import { TooltipProvider } from "src/shadcn/ui/tooltip";
import { PurchaseOrder } from "src/types";
import { MetricLine } from "./metricLine";

interface PurchaseOrderFulfilmentCompletionCellProps {
  purchaseOrder: PurchaseOrder;
  canViewWarehouseProgress: boolean;
}

export const PurchaseOrderFulfilmentCompletionCell: React.FC<PurchaseOrderFulfilmentCompletionCellProps> = ({
  purchaseOrder,
  canViewWarehouseProgress
}) => {
  const fulfilmentRate = useMemo(() => {
    if (typeof purchaseOrder.confirmationDetails?.fulfilmentRate === "number") {
      return purchaseOrder.confirmationDetails.fulfilmentRate;
    }
    const orderedTotal = purchaseOrder.products?.reduce((sum, product) => sum + (product.quantity ?? 0), 0) ?? 0;
    const confirmedTotal =
      purchaseOrder.confirmationDetails?.products?.reduce((sum, product) => sum + (product.confirmedQty ?? 0), 0) ?? 0;
    return orderedTotal > 0 ? (Math.min(confirmedTotal, orderedTotal) / orderedTotal) * 100 : 0;
  }, [
    purchaseOrder.confirmationDetails?.fulfilmentRate,
    purchaseOrder.products,
    purchaseOrder.confirmationDetails?.products
  ]);

  const warehouseMetrics = useMemo(() => {
    if (!canViewWarehouseProgress) {
      return {
        completionRate: 0
      };
    }

    const completionRate =
      typeof purchaseOrder.warehouseCompletionRate === "number" ? purchaseOrder.warehouseCompletionRate : 0;

    return {
      completionRate
    };
  }, [canViewWarehouseProgress, purchaseOrder.warehouseCompletionRate]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-w-[200px] flex-col">
        <MetricLine
          label="Supplier fulfilment"
          tooltip="Tracks how much of the originally ordered quantity has been confirmed by the supplier."
          percentage={fulfilmentRate}
          decimals={1}
        />
        <MetricLine
          label="Warehouse completion"
          tooltip="Measures how much of the expected quantity has physically arrived and been checked in by the warehouse."
          percentage={warehouseMetrics.completionRate}
          decimals={1}
        />
      </div>
    </TooltipProvider>
  );
};
